/**
 * Compact "Sign in with Google" screen rendered inside the embed iframe
 * when the user needs Google authentication (e.g. using support@gorgias.com).
 *
 * Opens a popup window at /embed/auth for the actual Google OAuth flow.
 */

import { useEffect, useMemo } from 'react';

const PREVIEW_DOMAIN = 'preview.gaia.gorgias-decision-engine.com';

/** On PR preview subdomains, return the bare preview domain origin for OAuth. */
function getAuthUrl(): string {
  const host = window.location.hostname;
  if (host.endsWith(`.${PREVIEW_DOMAIN}`) && host !== PREVIEW_DOMAIN) {
    return `https://${PREVIEW_DOMAIN}/embed/auth`;
  }
  return '/embed/auth';
}

interface SignInScreenProps {
  onAuthSuccess: () => void;
}

export function SignInScreen({ onAuthSuccess }: SignInScreenProps) {
  const authUrl = useMemo(() => getAuthUrl(), []);

  // Listen for the success postMessage from the popup (or fallback link).
  // Must be registered before the popup opens so we don't miss the message.
  useEffect(() => {
    const allowedOrigins = [window.location.origin, `https://${PREVIEW_DOMAIN}`];
    function handleMessage(event: MessageEvent) {
      if (
        allowedOrigins.includes(event.origin) &&
        event.data?.type === 'GAIA_EMBED_GOOGLE_AUTH_SUCCESS'
      ) {
        onAuthSuccess();
      }
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onAuthSuccess]);

  const handleSignIn = () => {
    // Pass the opener's origin so the popup knows where to post the token back
    const url = new URL(authUrl, window.location.origin);
    url.searchParams.set('opener_origin', window.location.origin);
    window.open(url.toString(), '_blank', 'popup,width=500,height=600');
  };

  return (
    <div className="flex h-screen items-center justify-center bg-background p-4">
      <div className="mx-auto w-full max-w-sm">
        <div className="rounded-xl border border-border bg-card p-8 shadow-lg space-y-6">
          {/* Icon */}
          <div className="flex justify-center">
            <img src="/gaia_logo.svg" alt="Gaia" className="size-10" />
          </div>

          {/* Content */}
          <div className="space-y-2 text-center">
            <h2 className="text-base font-semibold">Sign in to continue</h2>
            <p className="text-sm text-muted-foreground">
              Verify your identity with your Google workspace account to use Gaia.
            </p>
          </div>

          {/* Sign-in button */}
          <div className="flex flex-col items-center gap-3">
            <button
              onClick={handleSignIn}
              className="flex w-full cursor-pointer items-center justify-center gap-2.5 rounded-lg border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground shadow-sm transition-colors hover:bg-muted"
            >
              <svg className="size-[18px]" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Sign in with Google
            </button>

            {/* Fallback link if popup is blocked */}
            <a
              href={authUrl}
              target="_blank"
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Popup blocked? Click here to sign in
            </a>

          </div>
        </div>
      </div>
    </div>
  );
}
